import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPersonCarry } from '@fortawesome/pro-solid-svg-icons/faPersonCarry';
import React, { useCallback } from 'react';
import classnames from 'clsx';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_DURATIONS } from 'ui-components/services/notification-service';

import style from './style.module.css';
import { AddressFactory } from '../../address';
import { isAddressChanged } from '../../address/address-helpers';
import { Address } from '../../components/address';
import { AddressValidationSection } from '../../components/address-validation';
import { ComponentStates } from '../../enums/component-states';
import { validateAndUpdateAddress } from '../../fulfillment-plans/fulfillment-plans.api';
import { useSalesOrderRestrictionsBySOId } from '../../hooks/entities/selectors/sales-orders';
import { usePickupRelayOrderInfoBySalesOrderId } from '../../hooks/orders/use-pickup-relay-order-check';
import { ShipmentConfigurationService } from '../../services/shipping-configuration-service';
import Global from '../../store/global-selectors';
import { messages } from '../order-details/fulfillment-plan/sections/shipment-details/address/messages';
import { useUpdateEntitiesFulfillmentPlans } from '../order-details/hooks/use-update-entities-fulfillment-plans';

/**
 * @typedef {object} ShipToComponentProps
 * @property {string} [className=''] - CSS class name for the component wrapper
 * @property {string} [addressWrapperClassName=''] - CSS class name for the address wrapper
 * @property {string} [editAddressWrapperClassName=''] - CSS class name for the edit address wrapper
 * @property {boolean} [hideAddress3=false] - Whether to hide address line 3
 * @property {boolean} [isEdit] - Whether the component is in edit mode
 * @property {() => void} [notifyParent] - Callback function to notify parent component
 * @property {string} salesOrderId - Required sales order ID
 * @property {string} selectedFulfillmentPlanId - Required fulfillment plan ID
 */

/**
 * ShipTo component for displaying and editing shipping address information
 * @param {ShipToComponentProps} props
 * @returns {JSX.Element}
 */
export const ShipTo = ({
  className = '',
  addressWrapperClassName = '',
  editAddressWrapperClassName = '',
  hideAddress3 = false,
  isEdit,
  notifyParent = noop,
  salesOrderId,
  selectedFulfillmentPlanId,
}) => {
  const updateEntities = useUpdateEntitiesFulfillmentPlans();
  const isPickupRelayOrder = usePickupRelayOrderInfoBySalesOrderId(salesOrderId) !== null;
  const address =
    useSelector((state) =>
      Global.selectEntitiesFulfillmentPlanProperty(state, {
        fulfillmentPlanId: selectedFulfillmentPlanId,
        property: 'shipTo',
      }),
    ) || {};
  const email = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'soldTo.email',
    }),
  );
  // Use marketplace address (original imported address) for revert comparison
  const marketplaceShipTo = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'marketplaceShipTos.0',
    }),
  );
  const requestedService = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'requestedService',
    }),
  );
  const isFedexHal = ShipmentConfigurationService.isFedexHal(requestedService);
  const uiRestrictions = useSalesOrderRestrictionsBySOId(salesOrderId);
  const readOnlyShipToAddress = uiRestrictions?.shipTos?.componentState === ComponentStates.ReadOnly;
  const disableEditButton = isFedexHal || readOnlyShipToAddress;

  const showValidationError = useCallback(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.validationErrorTitle} />,
      content: <FormattedMessage {...messages.validationErrorMessage} />,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  }, []);

  const onSave = useCallback(
    async (editedAddress, editedEmail, autoCorrect) => {
      // When saving an edit in the Order Details is the only time we should lock the address
      const newAddress = {
        ...editedAddress,
        lockAddress: isAddressChanged(address, editedAddress),
      };

      try {
        const { data } = await validateAndUpdateAddress({
          shipTo: newAddress,
          fulfillmentPlanIds: [selectedFulfillmentPlanId],
          validateAddress: autoCorrect,
        });
        updateEntities(data);
      } catch {
        showValidationError();
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [selectedFulfillmentPlanId, updateEntities, showValidationError],
  );

  const showRevertError = useCallback(() => {
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.revertErrorTitle} />,
      content: <FormattedMessage {...messages.revertErrorMessage} />,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  }, []);

  const revertToOriginalAddress = useCallback(async () => {
    if (!marketplaceShipTo) {
      showRevertError();
      return;
    }
    try {
      const { data } = await validateAndUpdateAddress({
        shipTo: marketplaceShipTo,
        fulfillmentPlanIds: [selectedFulfillmentPlanId],
        validateAddress: true,
      });
      updateEntities(data);
    } catch {
      showRevertError();
    }
  }, [marketplaceShipTo, selectedFulfillmentPlanId, updateEntities, showRevertError]);

  const validateAddress = useCallback(
    async (addressToValidate) => {
      try {
        const { data } = await validateAndUpdateAddress({
          shipTo: addressToValidate,
          fulfillmentPlanIds: [selectedFulfillmentPlanId],
          validateAddress: true,
        });
        updateEntities(data);
      } catch {
        showValidationError();
      }
    },
    [selectedFulfillmentPlanId, updateEntities, showValidationError],
  );

  const validationComponent = (
    <AddressValidationSection
      countryCode={address.countryCode}
      marketplaceShipTo={marketplaceShipTo}
      residentialIndicator={address.residentialIndicator}
      revertToOriginalAddress={revertToOriginalAddress}
      shipToAddress={address}
      shouldRenderAddressRevertLink
      validateAddress={validateAddress}
      validationMessage='validate_address'
    />
  );

  return (
    <div className={classnames(style['address-factory-wrapper'], className)}>
      {isPickupRelayOrder ? (
        <div className={style['pickup-relay-info']}>
          <Address address={address} />
          {address?.line1 ? (
            <>
              <FontAwesomeIcon icon={faPersonCarry} />
              <span>
                <FormattedMessage
                  defaultMessage='Validated'
                  description='Ship to component PickupRelay order validated address text'
                  id='vPLDhi'
                />
              </span>
            </>
          ) : null}
        </div>
      ) : (
        <AddressFactory
          address={address}
          addressValidationComponent={!readOnlyShipToAddress && validationComponent}
          addressWrapperClassName={addressWrapperClassName}
          disableEditButton={disableEditButton}
          editAddressWrapperClassName={editAddressWrapperClassName}
          email={email}
          isEdit={isEdit}
          notifyParent={notifyParent}
          onSave={onSave}
          salesOrderId={salesOrderId}
          showAddressLine3={!hideAddress3}
          wrapperClassName={style['ship-to-address']}
        />
      )}
    </div>
  );
};
